import { ChangeDetectionStrategy, Component, computed, effect, input, output, signal } from '@angular/core';
import { NgClass } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';

@Component({
  selector: 'app-tolerance-bar',
  templateUrl: './tolerance-bar.component.html',
  styleUrls: ['./tolerance-bar.component.scss'],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [NgClass, FormsModule, TranslateModule],
})
export class ToleranceBarComponent {

  // ── Required inputs ────────────────────────────────────────────
  readonly asDesignedMax  = input.required<number>();
  readonly inToleranceMax = input.required<number>();

  // ── Optional display inputs ─────────────────────────────────────
  readonly label        = input<string>('');
  readonly unit         = input<string>('');
  readonly showError    = input<boolean>(false);
  readonly readOnly     = input<boolean>(false);
  /** When false the negative (left) side is hidden — use for pure-distance measurements. */
  readonly showNegative = input<boolean>(true);

  // ── User-controlled symmetry (only relevant when showNegative=true) ──
  protected readonly symmetric = signal(false);

  // ── Asymmetric min inputs (absolute magnitude of the negative boundary) ──
  readonly asDesignedMin  = input<number>(0);
  readonly inToleranceMin = input<number>(0);

  // ── Outputs ─────────────────────────────────────────────────────
  readonly asDesignedMaxChange  = output<number>();
  readonly inToleranceMaxChange = output<number>();
  readonly asDesignedMinChange  = output<number>();
  readonly inToleranceMinChange = output<number>();

  // ── Local editable state ────────────────────────────────────────
  protected readonly editAsDesigned    = signal(0);
  protected readonly editInTolerance   = signal(0);
  protected readonly editAsDesignedMin  = signal(0);
  protected readonly editInToleranceMin = signal(0);

  constructor() {
    effect(() => { this.editAsDesigned.set(this.asDesignedMax()); });
    effect(() => { this.editInTolerance.set(this.inToleranceMax()); });
    effect(() => { this.editAsDesignedMin.set(this.asDesignedMin()); });
    effect(() => { this.editInToleranceMin.set(this.inToleranceMin()); });
  }

  // ── Clamped inner values ────────────────────────────────────────

  protected readonly clampedInner = computed(() =>
    Math.min(this.editInTolerance() || 0, this.editAsDesigned() || 0)
  );

  protected readonly clampedInnerMin = computed(() =>
    Math.min(this.editInToleranceMin() || 0, this.editAsDesignedMin() || 0)
  );

  // ── Fixed tick positions (% left relative to .tb-ticks) ───────────
  // 5-box layout (showNegative=true):  20 | 40 | [center 50] | 60 | 80
  // 3-box layout (showNegative=false):                              33 | 67

  protected readonly pos1 = computed(() => 20);
  protected readonly pos2 = computed(() => 40);
  protected readonly pos3 = computed(() => this.showNegative() ? 60 : 100 / 3);
  protected readonly pos4 = computed(() => this.showNegative() ? 80 : 200 / 3);

  // ── Handlers ────────────────────────────────────────────────────

  private round1(value: number): number {
    return Math.round(+value * 10) / 10;
  }

  protected onAsDesignedInput(value: number): void        { this.editAsDesigned.set(this.round1(value)); }
  protected onInToleranceInput(value: number): void       { this.editInTolerance.set(this.round1(value)); }

  protected onAsDesignedMaxChange(value: number): void {
    const v = this.round1(value);
    this.editAsDesigned.set(v);
    this.asDesignedMaxChange.emit(v);
  }

  protected onInToleranceMaxChange(value: number): void {
    const v = this.round1(value);
    this.editInTolerance.set(v);
    this.inToleranceMaxChange.emit(v);
  }

  protected onAsDesignedMinChange(value: number): void {
    const v = this.round1(value);
    this.editAsDesignedMin.set(v);
    this.asDesignedMinChange.emit(v);
  }

  protected onInToleranceMinChange(value: number): void {
    const v = this.round1(value);
    this.editInToleranceMin.set(v);
    this.inToleranceMinChange.emit(v);
  }
}
